
import { VEmojiPicker } from 'v-emoji-picker';
import { getMedia } from '@/utils/mixins';
import MediaBar from '@/components/ui/molecule/MediaBar';
import MultipleImageDisplay from '@/components/ui/molecule/MultipleImageDisplay';
import PostBox from '@/components/ui/molecule/PostBox';
import { PostDisabledReason } from '~/types/models/post';
import GifSelectorModal from '../molecule/GifSelectorModal.vue';
import GifSelectorDrawer from '../molecule/GifSelectorDrawer.vue';
import { isMobileResponsive } from '@/utils/mixins';
import { GifTypes } from '~/types/models/gifTypes';

const GIF_TYPE_MAP = [
  { match: 'klipy.com', type: GifTypes.KLIPY },
  { match: 'giphy.com', type: GifTypes.GIPHY },
];

// TODO from Nikola: rename this component - drop the UI- prefix, introduce our Reply notion.
export default {
  name: 'UICreateComment',
  components: {
    VEmojiPicker,
    MediaBar,
    GifSelectorModal,
    MultipleImageDisplay,
    PostBox,
    GifSelectorDrawer,
  },
  mixins: [getMedia, isMobileResponsive],
  props: {
    searchMentions: {
      type: Function,
      required: true,
    },
    searchHashtags: {
      type: Function,
      required: true,
    },
    placeholder: {
      type: String,
      required: true,
    },
    clear: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    uploadImage: {
      type: Function,
      required: true,
    },
    target: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    profileAvatarUri: {
      type: String,
      default: null,
    },
    targetType: {
      type: String,
      required: false,
      default: 'POST',
    },
    showProfileImage: {
      type: Boolean,
      default: true,
    },
    topMargin: {
      type: Number,
      default: 6,
    },
    isEmailConfirmed: {
      type: Boolean,
      default: false,
    },
    league: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      editor: null,
      currentMedia: '',
      imagesToUpload: null,
      disabledByContent: false,
      disabledTooltip: null,
      gifToUpload: null,
      currentEditorIndex: null,
      /**
       * @description Inner width of the window
       * Used to determine if the user is on mobile or not.
       *
       * @default window.innerWidth
       * @type {number}
       */
      windowWidth: window.innerWidth,
      postBoxKey: 0,
      embed: null,
      hasText: false,
    };
  },
  computed: {
    imagesToDisplay() {
      if (this.gifToUpload) {
        return [{ src: this.gifToUpload.uri }];
      } else if (this.imagesToUpload) {
        return this.imagesToUpload.map((image) => ({
          src: image.preview,
        }));
      } else {
        return null;
      }
    },
    disabledMedia() {
      if (this.gifToUpload) {
        return ['image'];
      } else if (this.imagesToUpload && this.imagesToUpload.length) {
        return ['gif'];
      }

      if (this.disabled) {
        return ['image', 'gif', 'emoji'];
      }
      return [];
    },
    isMobile() {
      return this.windowWidth <= 768;
    },
    getContainerClasses() {
      return `mt-${this.topMargin}`;
    },
    hasContent() {
      const hasMedia =
        !!this.gifToUpload ||
        (this.imagesToUpload && this.imagesToUpload.length > 0);
      return this.hasText || hasMedia;
    },
  },
  watch: {
    clear() {
      if (this.clear) {
        this.editor.setText('');
        this.imagesToUpload = null;
        this.gifToUpload = null;
        this.currentMedia = '';
        this.hasText = false;
      }
    },
    reply: {
      immediate: true,
      handler(newPost) {
        if (newPost) {
          this.setMediaForPostUpdate();
        }
      },
    },
  },
  mounted() {
    // Update window width on resize so that the computed property is updated
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
  },
  methods: {
    resetPostBox() {
      this.postBoxKey += 1;
      this.hasText = false;
    },
    getGifType() {
      const uri = this.gifToUpload?.uri;
      if (!uri) {
        return null;
      }

      const match = GIF_TYPE_MAP.find((item) => uri.includes(item.match));

      return match ? match.type : null;
    },
    submitPost() {
      const images = this.getImageFormData();
      const mentions = this.getMentions();
      const body = this.getBody(mentions);
      const gifType = this.getGifType();

      if (body || images || this.gifToUpload || mentions.length) {
        const payload = {
          body: body,
          mentions,
          gifUri: this.gifToUpload ? this.gifToUpload.uri : null,
          gifType,
          images,
          targetType: this.targetType,
          target: this.target,
          embed: this.embed,
          leagueId: this.league?.leagueId,
        };
        this.$emit('onSubmit', payload);
        this.resetPostBox();
      }
    },
    getBody(mentions = []) {
      let body = this.editor
        .getContents()
        .ops.map((op) => {
          if (typeof op.insert === 'string') {
            return op.insert;
          }
          if (op.insert.mention) {
            return op.insert.mention.denotationChar + op.insert.mention.value;
          }
        })
        .join('')
        .replace(/\n$/, '');

      mentions.forEach((mention) => {
        if (mention.type === '@') {
          body = body.replace(mention.value, mention.target);
        }
      });
      return body;
    },
    getMentions() {
      const contents = this.editor.getContents();
      const res = [];

      contents.forEach((e) => {
        if (e.insert && e.insert.mention) {
          res.push({
            type: e.insert.mention.denotationChar,
            target: e.insert.mention.id,
            value: e.insert.mention.value,
          });
        }
      });

      if (this.mentionedPost) {
        res.push({ type: 'POST', target: this.mentionedPost.postId });
      }

      return res;
    },
    getImageFormData() {
      if (this.imagesToUpload && this.imagesToUpload.length) {
        return this.imagesToUpload.map((e) => e.id);
      } else {
        return null;
      }
    },
    async handleImage(file) {
      if ((this.imagesToUpload || []).length + 1 > 4) {
        this.$notify.error(this.$t('max_image_to_upload'));
      } else {
        file = await this.uploadImage(file);
        this.imagesToUpload = [...(this.imagesToUpload || []), file];
      }
    },
    removeImage(index) {
      if (this.gifToUpload) {
        this.gifToUpload = null;
      } else {
        this.$emit('removeImage', this.imagesToUpload[index].id);
        this.imagesToUpload.splice(index, 1);
      }
    },
    handleSelectMedia(selectedMedia) {
      if (this.currentMedia === selectedMedia) {
        this.currentMedia = '';
      } else {
        this.currentMedia = selectedMedia;
      }
    },
    selectEmoji(emoji) {
      const tmpIndex = this.currentEditorIndex || 0;
      this.editor.insertText(this.currentEditorIndex || 0, emoji.data, 'user');
      this.currentEditorIndex = tmpIndex + emoji.data.length;
      this.currentMedia = '';
    },
    handleClickOutsideEmojiPicker() {
      this.currentMedia = '';
    },
    onClickGIF(gif) {
      this.gifToUpload = gif;
      this.currentMedia = '';
    },
    calcIndexWithMentions() {
      const textLength =
        this.editor.getText().length <= 0
          ? 0
          : this.editor.getText().length - 1;
      return textLength + this.getMentions().length;
    },
    getEditor(editor) {
      this.editor = editor;
      this.editor.on('text-change', () => {
        this.currentEditorIndex = this.calcIndexWithMentions();
        this.hasText = this.editor.getText().trim().length > 0;
      });
      this.editor.on('selection-change', (selection) => {
        if (selection) {
          this.currentEditorIndex = selection.index;
        }
      });
    },
    toggleDisable(disable) {
      this.disabledByContent = disable.disabled;
      switch (disable.reason) {
        case PostDisabledReason.CONTENT:
          this.disabledTooltip = this.$t('post_disabled_bad_content');
          break;
        case PostDisabledReason.LENGTH:
          this.disabledTooltip = this.$t('post_disabled_length');
          break;
        default:
          return;
      }
    },
    updatePost() {
      const images = this.getImageFormData();
      const mentions = this.getMentions();
      const body = this.getBody(mentions);
      const gifType = this.getGifType();

      if (body || mentions.length) {
        this.$emit('onUpdate', {
          postId: this.reply.postId,
          body: body,
          mentions,
          gifUri: this.gifToUpload ? this.gifToUpload.uri : null,
          gifType,
          images,
          embed: this.embed,
          targetType: this.targetType,
          target: this.target,
          updatedDate: new Date().toISOString(),
        });
      }
    },
    handleCancelEdit(event) {
      this.$emit('onCancelEdit');
      event.stopPropagation();
    },
    async setMediaForPostUpdate() {
      if (this.reply?.images.length) await this.getImagePreview(this.reply);
      else {
        const media = this.getMedia(this.reply);
        if (!media) return;

        this.gifToUpload = { id: this.reply?.gifUri, src: media[0].src };
      }
    },
    async getImagePreview(post) {
      this.imagesToUpload = [...post.images];

      if (this.imagesToUpload[0]) {
        const res = await this.getPostImageForPreview(
          this.imagesToUpload[0].uri
        );

        const updatedImage = { ...this.imagesToUpload[0], preview: res.data };
        this.imagesToUpload = [updatedImage, ...this.imagesToUpload.slice(1)];
      }
    },
  },
};
